import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ChevronRight, ChevronDown, Tag, Filter, X } from "lucide-react";

/**
 * Sidebar de filtros por Categoría y Sub-categoría.
 *
 * Props:
 *   categoryId       — id seleccionado (o null = "Todas")
 *   subcategoryId    — id de sub seleccionado (o null)
 *   onChange({ category_id, subcategory_id })  — callback al cambiar
 *   testidPrefix     — prefijo para data-testid
 *
 * Comportamiento:
 *   - Click en una categoría → filtra por esa categoría (sub queda null).
 *   - Click en una sub → filtra por sub (y mantiene la categoría padre).
 *   - Click en "Todas" → limpia ambos.
 *   - Categorías expansibles para revelar sus subcategorías.
 */
export default function CategoryFilter({
  categoryId = null,
  subcategoryId = null,
  onChange,
  testidPrefix = "catfilter",
}) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/subcategories")]).then(([c, s]) => {
      setCategories(c.data.filter((x) => x.active !== false));
      setSubcategories(s.data.filter((x) => x.active !== false));
    });
  }, []);

  // Expand parent automatically if a sub is selected from outside
  useEffect(() => {
    if (subcategoryId) {
      const sub = subcategories.find((s) => s.id === subcategoryId);
      if (sub?.category_id) {
        setExpanded((prev) => new Set([...prev, sub.category_id]));
      }
    }
  }, [subcategoryId, subcategories]);

  const subsByCat = useMemo(() => {
    const map = {};
    for (const s of subcategories) {
      const k = s.category_id || "__orphan";
      if (!map[k]) map[k] = [];
      map[k].push(s);
    }
    return map;
  }, [subcategories]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isAll = !categoryId && !subcategoryId;
  const orphanSubs = subsByCat["__orphan"] || [];

  return (
    <aside className="bg-card border border-border rounded-md p-2 w-full lg:w-56 shrink-0" data-testid={`${testidPrefix}-sidebar`}>
      <div className="px-2 py-1.5 flex items-center justify-between">
        <div className="overline flex items-center gap-1.5">
          <Filter className="w-3 h-3" /> Categorías
        </div>
        {!isAll && (
          <button
            onClick={() => onChange({ category_id: null, subcategory_id: null })}
            className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
            data-testid={`${testidPrefix}-clear`}
            title="Limpiar filtros"
          >
            <X className="w-3 h-3" /> Limpiar
          </button>
        )}
      </div>

      <button
        onClick={() => onChange({ category_id: null, subcategory_id: null })}
        className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
          isAll ? "bg-primary/10 text-primary font-bold" : "hover:bg-accent"
        }`}
        data-testid={`${testidPrefix}-all`}
      >
        Todas
      </button>

      <div className="mt-1 max-h-[calc(100vh-22rem)] overflow-y-auto">
        {categories.map((c) => {
          const isOpen = expanded.has(c.id);
          const isCatActive = categoryId === c.id && !subcategoryId;
          const childSubs = subsByCat[c.id] || [];
          return (
            <div key={c.id}>
              <div className="flex items-stretch gap-0.5">
                <button
                  onClick={() => toggle(c.id)}
                  className="p-1.5 hover:bg-accent rounded transition-colors shrink-0"
                  data-testid={`${testidPrefix}-toggle-${c.id}`}
                  aria-label={isOpen ? "Colapsar" : "Expandir"}
                  disabled={childSubs.length === 0}
                >
                  {childSubs.length > 0
                    ? (isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />)
                    : <Tag className="w-3 h-3 text-muted-foreground" />}
                </button>
                <button
                  onClick={() => onChange({ category_id: c.id, subcategory_id: null })}
                  className={`flex-1 text-left px-2 py-1.5 rounded text-sm transition-colors truncate ${
                    isCatActive ? "bg-primary/10 text-primary font-bold" : "hover:bg-accent"
                  }`}
                  data-testid={`${testidPrefix}-cat-${c.id}`}
                  title={c.name}
                >
                  {c.name}
                </button>
              </div>
              {isOpen && childSubs.length > 0 && (
                <div className="ml-5 border-l border-border/60 my-0.5">
                  {childSubs.map((s) => {
                    const isSubActive = subcategoryId === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => onChange({ category_id: c.id, subcategory_id: s.id })}
                        className={`block w-full text-left pl-3 pr-2 py-1 rounded-r text-xs transition-colors truncate ${
                          isSubActive ? "bg-primary/10 text-primary font-bold border-l-2 border-primary -ml-px" : "hover:bg-accent text-muted-foreground"
                        }`}
                        data-testid={`${testidPrefix}-sub-${s.id}`}
                        title={s.name}
                      >
                        ↳ {s.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {orphanSubs.length > 0 && (
          <>
            <div className="overline mt-3 px-2 py-1">Sin categoría</div>
            {orphanSubs.map((s) => (
              <button
                key={s.id}
                onClick={() => onChange({ category_id: null, subcategory_id: s.id })}
                className={`block w-full text-left px-2 py-1 rounded text-xs transition-colors ${
                  subcategoryId === s.id ? "bg-primary/10 text-primary font-bold" : "hover:bg-accent text-muted-foreground"
                }`}
              >
                {s.name}
              </button>
            ))}
          </>
        )}
      </div>
    </aside>
  );
}
